import type { ReactNode } from "react";
import { AD_BUTTONS } from "../../../shared/goals/ready";
import { Icon } from "../../components/ui/Icon";
import { readyApi, type ReadyAd } from "../../lib/readyApi";
import { ProductMark, type ProductRef } from "../goal/FlowHead";
import { AdPlayer } from "./AdPlayer";

/**
 * An ad drawn the way Facebook and Instagram draw it, in a phone: the feed white and Stories and Reels black,
 * whatever the app's own theme. The owner's real video or photo plays in it.
 */

export type PreviewView = "feed" | "stories";

const isImage = (ad: ReadyAd) => (ad.asset?.contentType ?? "").startsWith("image/") || ad.words.format === "image";

function Head({ product, dark }: { product: ProductRef | null; dark?: boolean }) {
  return (
    <div className={dark ? "ad-fbh dk" : "ad-fbh"}>
      <span className="ad-fba">{product ? <ProductMark product={product} /> : null}</span>
      <span className="ad-fbn">
        <b>{product?.name ?? "Your Page"}</b>
        <span>
          Sponsored · <Icon name="globe" />
        </span>
      </span>
      <span className="ad-fbx">
        {!dark && <Icon name="more" />}
        <Icon name="x" />
      </span>
    </div>
  );
}

/** The ad's picture or video in its frame, or the empty frame that says what goes there. */
function Media({ goalId, ad, tall }: { goalId: string; ad: ReadyAd; tall?: boolean }) {
  const image = isImage(ad);
  const slot = tall && ad.files.tall ? "tall" : "main";
  if (!ad.asset)
    return (
      <div className={tall ? "ad-med empty tall" : "ad-med empty"}>
        <Icon name="film" />
        <span>{tall ? (image ? "Your upright photo shows here" : "Your upright video shows here") : image ? "Your photo shows here" : "Your video shows here"}</span>
      </div>
    );
  const src = readyApi.videoUrl(goalId, ad.id, ad.rev, slot);
  const label = !tall ? "The ad's video" : ad.files.tall ? "The Stories and Reels version" : "The ad's video, as Stories and Reels show it";
  return <div className={tall ? "ad-med tall" : image ? "ad-med sq" : "ad-med"}>{image ? <img src={src} alt="" /> : <AdPlayer src={src} label={label} fill={tall} />}</div>;
}

/** The feed post: the Page, the words, the picture, the headline with its button, and Like, Comment and Share. */
export function FeedCard({ goalId, ad, product, opens, text, headline, mini, writing }: { goalId: string; ad: ReadyAd; product: ProductRef | null; opens: string; text: string; headline: string; mini?: boolean; writing?: ReactNode }) {
  const w = ad.words;
  const format = w.format ?? "camera";
  const cta = AD_BUTTONS[w.cta ?? "SIGN_UP"];
  const cards = w.cards ?? [];
  return (
    <div className="ad-fb">
      <Head product={product} />
      <p className="ad-fbt">{writing ?? (format === "post" ? w.post?.text : text)}</p>
      {mini ? null : format === "carousel" ? (
        <div className="ad-car">
          {cards.map((c, i) => (
            <div className="ad-carc" key={c.id}>
              <div className="ad-carm">{ad.files.cards[c.id] ? <img src={readyApi.videoUrl(goalId, ad.id, ad.rev, `card:${c.id}`)} alt="" /> : <span>Card {i + 1}</span>}</div>
              <div className="ad-carf">
                <b>{c.headline || "..."}</b>
                <span className="ad-cta">{cta}</span>
              </div>
            </div>
          ))}
        </div>
      ) : format === "post" ? (
        <div className="ad-med sq">{w.post?.picture ? <img src={w.post.picture} alt="" referrerPolicy="no-referrer" /> : <span className="ad-pk">{w.post?.kind}</span>}</div>
      ) : (
        <Media goalId={goalId} ad={ad} />
      )}
      {format !== "carousel" && (
        <div className="ad-fbl">
          <span className="ad-fbu">
            <span>{(opens || "Form on Facebook").toUpperCase()}</span>
            <b>{writing ? "..." : headline}</b>
          </span>
          <span className="ad-cta">{cta}</span>
        </div>
      )}
      {!mini && format === "post" && w.post && (
        <div className="ad-fbr">
          <span>{w.post.reactions} reactions</span>
          <span>{w.post.comments} comments</span>
        </div>
      )}
      {!mini && (
        <div className="ad-fba2">
          <span>
            <Icon name="adThumb" />
            Like
          </span>
          <span>
            <Icon name="adChat" />
            Comment
          </span>
          <span>
            <Icon name="adShare" />
            Share
          </span>
        </div>
      )}
    </div>
  );
}

/** Stories and Reels: the upright frame, the Page on top, the headline and the button at the bottom. */
function StoryCard({ goalId, ad, product }: { goalId: string; ad: ReadyAd; product: ProductRef | null }) {
  const cta = AD_BUTTONS[ad.words.cta ?? "SIGN_UP"];
  return (
    <div className="ad-sty">
      <Media goalId={goalId} ad={ad} tall />
      <div className="ad-stb" aria-hidden="true">
        <i className="on" />
        <i />
        <i />
      </div>
      <Head product={product} dark />
      <div className="ad-stf">
        <b>{ad.words.headline ?? ""}</b>
        <span className="ad-stc">
          <Icon name="chev" />
          <span>{cta}</span>
        </span>
      </div>
    </div>
  );
}

/** The phone, drawn small, with the ad in its feed or in Stories and Reels. */
export function AdPhone({ goalId, ad, product, opens, view, writing }: { goalId: string; ad: ReadyAd; product: ProductRef | null; opens: string; view: PreviewView; writing?: ReactNode }) {
  return (
    <div className="ad-pho" role="img" aria-label={view === "stories" ? "Preview: Stories and Reels" : "Preview: the feed"}>
      <div className={view === "stories" ? "ad-phs dk" : "ad-phs"}>
        {view === "stories" ? (
          <StoryCard goalId={goalId} ad={ad} product={product} />
        ) : (
          <>
            <div className="ad-feedtop" />
            <FeedCard goalId={goalId} ad={ad} product={product} opens={opens} text={ad.words.primary ?? ""} headline={ad.words.headline ?? ""} writing={writing} />
            <div className="ad-fbnext">
              <Head product={product} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** A small square of the ad for its row: its picture, the first frame of its video, or what it is. */
export function AdThumb({ goalId, ad }: { goalId: string; ad: ReadyAd }) {
  const w = ad.words;
  const firstCard = (w.cards ?? []).find((c) => ad.files.cards[c.id]);
  let inner: ReactNode = <Icon name={w.format === "carousel" ? "adGrid" : "film"} />;
  if (w.format === "post" && w.post?.picture) inner = <img src={w.post.picture} alt="" referrerPolicy="no-referrer" />;
  else if (firstCard) inner = <img src={readyApi.videoUrl(goalId, ad.id, ad.rev, `card:${firstCard.id}`)} alt="" />;
  else if (ad.asset && isImage(ad)) inner = <img src={readyApi.videoUrl(goalId, ad.id, ad.rev)} alt="" />;
  else if (ad.asset)
    inner = (
      <>
        <video src={`${readyApi.videoUrl(goalId, ad.id, ad.rev)}#t=0.001`} muted playsInline preload="metadata" />
        <span className="ad-thp">
          <Icon name="play" />
        </span>
      </>
    );
  return <span className={ad.asset || firstCard || w.post?.picture ? "ad-th has" : "ad-th"}>{inner}</span>;
}
